import Delta, { type Op } from "quill-delta";
import type { TextAlign } from "../typesV2";

function normalizeAlignValue(value: unknown): TextAlign | undefined {
    if (typeof value !== "string") {
        return undefined;
    }

    const normalized = value.trim().toLowerCase();

    if (normalized === "right") {
        return "right";
    }

    if (normalized === "center") {
        return "center";
    }

    return undefined;
}

function isPureNewlineInsert(op: Op): boolean {
    return typeof op.insert === "string" && op.insert === "\n";
}

export function sanitizeDeltaAlign(delta: Delta) {
    if (!delta.ops || !Array.isArray(delta.ops)) {
        return new Delta();
    }

    const sanitizedOps: Op[] = delta.ops.map((op) => {
        const nextOp: Op = { ...op };

        if (!nextOp.attributes || !("align" in nextOp.attributes)) {
            return nextOp;
        }

        const nextAttributes: Record<string, unknown> = { ...nextOp.attributes };
        const normalizedAlign = normalizeAlignValue(nextAttributes.align);

        const shouldKeepAlign = isPureNewlineInsert(nextOp) && normalizedAlign !== undefined;

        if (shouldKeepAlign) {
            nextAttributes.align = normalizedAlign;
        } else {
            delete nextAttributes.align;
        }

        nextOp.attributes = Object.keys(nextAttributes).length > 0 ? nextAttributes : undefined;

        return nextOp;
    });

    return new Delta(sanitizedOps);
}

export function sanitizeDeltaLinkColor(delta: Delta) {
    if (!delta.ops || !Array.isArray(delta.ops)) {
        return new Delta();
    }

    const sanitizedOps: Op[] = delta.ops.map((op) => {
        if (!op.attributes || typeof op.attributes !== "object") {
            return op;
        }

        if (!op.attributes.link || !("color" in op.attributes)) {
            return op;
        }

        const nextAttributes = { ...op.attributes };
        delete nextAttributes.color;

        return {
            ...op,
            attributes: Object.keys(nextAttributes).length > 0 ? nextAttributes : undefined,
        };
    });

    return new Delta(sanitizedOps);
}

export function enforceDeltaColor(delta: Delta, color: string) {
    if (!delta.ops || !Array.isArray(delta.ops)) {
        return new Delta();
    }

    const normalizedOps: Op[] = delta.ops.map((op) => {
        if (typeof op.insert !== "string") {
            return op;
        }

        if (op.insert.replace(/\n/g, "").length <= 0) {
            return op;
        }

        if (op.attributes?.link) {
            return op;
        }

        return {
            ...op,
            attributes: {
                ...(op.attributes ?? {}),
                color,
            },
        };
    });

    return new Delta(normalizedOps);
}
